import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import type {
  DesignBuilding,
  DoorTexture,
  Structure,
  TemplateRoofAppearance,
  TemplateWallAppearance,
  TemplateWindowAppearance,
  TextureAppearance,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { DoorGroup } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import type {
  BuildingAppearances,
  BuildingMutations,
} from 'src/modules/designStudio/providers/MacheteBuildingProvider/types';

const DEFAULT_BUILDING_APPEARANCE: BuildingAppearances = {
  roofAppearances: [],
  wallAppearances: [],
  trimAppearances: [],
  windowAppearances: [],
  doorCounts: {
    [DoorGroup.FRONT]: {
      count: 0,
    },
    [DoorGroup.SERVICE]: {
      count: 0,
    },
    [DoorGroup.GARAGE]: {
      count: 0,
    },
    [DoorGroup.SLIDING_GLASS]: {
      count: 0,
    },
  },
};

const DEFAULT_BUILDING_MUTATIONS: BuildingMutations = {
  changeWallAppearances: () => new Promise(() => {}),
  changeRoofAppearances: () => new Promise(() => {}),
  changeTrimAppearances: () => new Promise(() => {}),
  changeWindowAppearances: () => new Promise(() => {}),
  changeDoors: () => new Promise(() => {}),
  applyDesignConfig: () => new Promise(() => {}),
  applyPalette: () => new Promise(() => {}),
};

export const createBuildingMutations = (
  building: DesignBuilding | undefined,
): BuildingMutations => {
  if (!building) {
    return DEFAULT_BUILDING_MUTATIONS;
  }

  const activeDesign = building.designer.get_active_design();

  return {
    changeWallAppearances: async (
      before: TemplateWallAppearance[],
      after: TemplateWallAppearance,
    ) => activeDesign.change_wall_appearances(before, after),

    changeRoofAppearances: async (
      before: TemplateRoofAppearance[],
      after: TemplateRoofAppearance,
    ) => activeDesign.change_roof_appearances(before, after),

    changeTrimAppearances: async (
      before: TextureAppearance[],
      after: TextureAppearance,
    ) => activeDesign.change_trim_appearances(before, after),

    changeWindowAppearances: async (
      before: TemplateWindowAppearance[],
      after: TemplateWindowAppearance,
    ) => activeDesign.change_window_appearances(before, after),

    changeDoors: async (
      group: DoorGroup,
      textures: DoorTexture[],
      color: string | undefined,
      tol: number,
    ) => {
      switch (group) {
        case DoorGroup.FRONT:
          await activeDesign.change_front_doors(textures, color, tol);
          break;
        case DoorGroup.SERVICE:
          await activeDesign.change_service_doors(textures, color, tol);
          break;
        case DoorGroup.GARAGE:
          await activeDesign.change_garage_doors(textures, color, tol);
          break;
        default:
          break;
      }
    },

    applyDesignConfig: (designConfig: Structure) =>
      building.designer.set_active_design(designConfig),
    applyPalette: (palette: TemplatePaletteConfig) =>
      activeDesign.apply_palette(palette),
  };
};

export const createBuildingAppearances = (
  building: DesignBuilding | undefined,
): BuildingAppearances => {
  if (!building) {
    return DEFAULT_BUILDING_APPEARANCE;
  }

  const activeDesign = building.designer.get_active_design();
  return {
    roofAppearances: activeDesign.get_roof_appearances(),
    wallAppearances: activeDesign.get_wall_appearances(),
    trimAppearances: activeDesign.get_trim_appearances(),
    windowAppearances: activeDesign.get_window_appearances(),
    doorCounts: activeDesign.get_door_counts(),
  } as BuildingAppearances;
};
